import React, { useState, useCallback, useEffect } from 'react';
import { usageTrackingService } from '../services/usageTracking';

const TUTORIAL_STEPS = [
  // Phase 1: Introduction
  {
    id: 'intro',
    title: 'Welcome to Tater',
    content: (
      <div>
        <h2 style={{ color: '#72d2ff', marginBottom: '12px' }}>Welcome to Tater</h2>
        <p style={{ marginBottom: '16px' }}>
          Tater has three main modes:
        </p>
        <div style={{
          background: '#0a1e38',
          border: '1px solid rgba(114, 210, 255, 0.25)',
          borderRadius: '6px',
          padding: '12px',
          marginTop: '12px'
        }}>
          <div style={{ color: '#ffffff', fontSize: '14px', lineHeight: '1.8' }}>
            <div><strong style={{ color: '#72d2ff' }}>1) Ball Mode:</strong> Label ball events (e.g. stroke, bounces)</div>
            <div><strong style={{ color: '#ffffff' }}>2) Player Mode:</strong> Label player's bounding boxes</div>
            <div><strong style={{ color: '#50fa7b' }}>3) Court Mode:</strong> Label court keypoints</div>
          </div>
        </div>
      </div>
    ),
    highlightElement: null,
    allowBackdropClick: false,
  },

  // Phase 2: Video Loading
  {
    id: 'video-loading',
    title: 'Loading a Video',
    content: (
      <div>
        <h2 style={{ color: '#72d2ff', marginBottom: '12px' }}>Loading a Video</h2>
        <p style={{ marginBottom: '16px' }}>
          To get started, you need to load a video. You can:
        </p>
        <ul style={{ marginLeft: '20px', marginBottom: '16px', lineHeight: '1.8' }}>
          <li>Paste a video URL in the input field</li>
          <li>Upload a video file from your computer</li>
          <li>Click "Demo Video" to load a sample video</li>
          <li>Click "Azure Storage" to load videos from there</li>
        </ul>
      </div>
    ),
    highlightElement: 'video-loading-section',
    tooltip: 'Use this section to load videos',
    tooltipPosition: 'right',
    allowBackdropClick: false,
  },

  // AI Assist explanation (moved earlier)
  {
    id: 'ai-assist-intro',
    title: 'AI Assist',
    content: (
      <div>
        <h2 style={{ color: '#72d2ff', marginBottom: '12px' }}>AI Assist</h2>
        <p style={{ marginBottom: '16px' }}>
          Check the "AI Assist" box to enable automatic player detection and court calibration. This allows automatic conversion of pixel to court coordinates in meters.
        </p>
      </div>
    ),
    highlightElement: 'ai-assist',
    tooltip: 'Enable AI-powered player detection and court calibration',
    tooltipPosition: 'left',
    allowBackdropClick: false,
  },

  // Phase 3: Understanding Modes - Ball Mode
  {
    id: 'ball-mode-intro',
    title: 'Ball Mode - Labeling Ball Events',
    content: (
      <div>
        <h2 style={{ color: '#ffb86c', marginBottom: '12px' }}>Ball Mode</h2>
        <p style={{ marginBottom: '16px' }}>
          Ball Mode is the default mode for labeling ball events. You can annotate:
        </p>
        <div style={{
          background: '#0a1e38',
          border: '1px solid rgba(114, 210, 255, 0.25)',
          borderRadius: '6px',
          padding: '12px',
          marginBottom: '16px',
          minWidth: '500px'
        }}>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            <div><strong style={{ color: '#ffb86c' }}>Strokes:</strong> When a player hits the ball</div>
            <div><strong style={{ color: '#8be9fd' }}>Bounces:</strong> When the ball bounces on the court</div>
            <div><strong style={{ color: '#ff79c6' }}>In-Flight:</strong> When the ball is in the air</div>
            <div><strong style={{ color: '#bd93f9' }}>Net:</strong> When the ball hits the net</div>
          </div>
        </div>
      </div>
    ),
    highlightElement: 'mode-toggle',
    tooltip: 'This toggle switches between Ball, Player, and Court modes',
    tooltipPosition: 'bottom',
    allowBackdropClick: false,
  },

  {
    id: 'ball-mode-annotation',
    title: 'Adding Ball Annotations',
    content: (
      <div>
        <h2 style={{ color: '#ffb86c', marginBottom: '12px' }}>Adding Annotations</h2>
        <p style={{ marginBottom: '16px' }}>
          To add ball annotations:
        </p>
        <ol style={{ marginLeft: '20px', marginBottom: '16px', lineHeight: '1.8' }}>
          <li>Make sure the video is paused (press Space to pause)</li>
          <li>Click on the video where you want to add an annotation</li>
        </ol>
        <p style={{ fontSize: '14px', marginBottom: '16px' }}>
          <strong style={{ color: '#50fa7b' }}>Left-click</strong> for bottom player strokes, <strong style={{ color: '#ff5555' }}>right-click</strong> for top player strokes.
        </p>
        <div style={{
          background: '#0a1e38',
          border: '1px solid rgba(114, 210, 255, 0.25)',
          borderRadius: '6px',
          padding: '12px',
          marginTop: '12px',
          minWidth: '500px'
        }}>
          <p style={{ fontSize: '14px', margin: 0, lineHeight: '1.8' }}>
            <strong>Tip:</strong> Press <strong style={{ color: '#52c3a3' }}>H</strong> (forehand), <strong style={{ color: '#db722c' }}>J</strong> (backhand),
            <strong style={{ color: '#f8f9f2' }}> K</strong> (serve), or <strong style={{ color: '#f1fa8c' }}>L</strong> (smash) to set stroke type. When "AI Assist" is enabled, player bounding boxes are added automatically as well as a guess for the stroke type.
          </p>
        </div>
      </div>
    ),
    highlightElement: 'video-player',
    tooltip: 'Click on the video to add annotations when paused',
    tooltipPosition: 'top',
    allowBackdropClick: false,
  },

  // Phase 3: Player Mode
  {
    id: 'player-mode-intro',
    title: 'Player Mode - Tracking Players',
    content: (
      <div>
        <h2 style={{ color: '#ffffff', marginBottom: '12px' }}>Player Mode</h2>
        <p style={{ marginBottom: '16px' }}>
          Player Mode allows you to track player positions by adding bounding boxes around players.
        </p>
        <div style={{
          background: '#0a1e38',
          border: '1px solid rgba(114, 210, 255, 0.25)',
          borderRadius: '6px',
          padding: '12px',
          marginBottom: '16px'
        }}>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            <div><strong>Press <strong style={{ color: '#50fa7b' }}>B</strong>:</strong> Add bounding box for bottom player</div>
            <div><strong>Press <strong style={{ color: '#ff5555' }}>T</strong>:</strong> Add bounding box for top player</div>
            <div><strong>Press <strong>Shift+B</strong> or <strong>Shift+T</strong>:</strong> Remove player bounding box</div>
          </div>
        </div>
      </div>
    ),
    highlightElement: 'mode-toggle',
    tooltip: 'Switch to Player Mode to track player positions',
    tooltipPosition: 'bottom',
    allowBackdropClick: false,
  },

  // Phase 3: Court Mode
  {
    id: 'court-mode-intro',
    title: 'Court Mode - Annotating Court Keypoints',
    content: (
      <div>
        <h2 style={{ color: '#50fa7b', marginBottom: '12px' }}>Court Mode</h2>
        <p style={{ marginBottom: '16px' }}>
          Court Mode allows you to label court keypoints.
        </p>
        <div style={{
          background: '#0a1e38',
          border: '1px solid rgba(114, 210, 255, 0.25)',
          borderRadius: '6px',
          padding: '12px',
          marginBottom: '16px',
          minWidth: '400px'
        }}>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            <div><strong>Press <strong style={{ color: '#50fa7b' }}>C</strong> in Court Mode:</strong> Predict court keypoints for current frame</div>
            <div><strong>Press <strong>Ctrl+C</strong>:</strong> Copy previous court annotation to current frame</div>
            <div><strong>Click and drag:</strong> Adjust keypoint positions manually</div>
          </div>
        </div>
        <p style={{ fontSize: '14px' }}>
          <strong>Tip:</strong> After labeling court keypoints, you can use the "Recalibrate" button to re-do conversion of pixel to court coordinates in meters.
        </p>
      </div>
    ),
    highlightElement: 'mode-toggle',
    tooltip: 'Switch to Court Mode to annotate court keypoints',
    tooltipPosition: 'bottom',
    allowBackdropClick: false,
  },

  // Phase 4: Navigation and Controls
  {
    id: 'navigation',
    title: 'Navigation and Controls',
    content: (
      <div>
        <h2 style={{ color: '#72d2ff', marginBottom: '12px' }}>Navigation and Controls</h2>
        <p style={{ marginBottom: '16px' }}>
          Use these controls to navigate through your video and annotations:
        </p>
        <div style={{
          background: '#0a1e38',
          border: '1px solid rgba(114, 210, 255, 0.25)',
          borderRadius: '6px',
          padding: '12px',
          marginBottom: '16px',
          minWidth: '500px'
        }}>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            <div><strong>Space:</strong> Play/Pause video</div>
            <div><strong>← →:</strong> Frame by frame navigation</div>
            <div><strong>Shift+← →:</strong> Skip 50 frames</div>
            <div><strong>Previous/Next buttons:</strong> Jump to previous/next annotation</div>
            <div><strong>Zoom controls:</strong> Zoom in/out on timeline and video</div>
          </div>
        </div>
        <p style={{ fontSize: '14px' }}>
          The timeline at the bottom shows all your annotations. Click on it to jump to any point in the video.
        </p>
      </div>
    ),
    highlightElement: 'video-controls',
    tooltip: 'Use these controls to navigate and play your video',
    tooltipPosition: 'top',
    allowBackdropClick: false,
  },

  // Phase 5: Advanced Features
  {
    id: 'keyboard-shortcuts',
    title: 'Keyboard Shortcuts',
    content: (
      <div>
        <h2 style={{ color: '#72d2ff', marginBottom: '12px' }}>Keyboard Shortcuts</h2>
        <p style={{ marginBottom: '16px' }}>
          Tater has extensive keyboard shortcuts to speed up your workflow. Some examples include:
        </p>
        <div style={{
          background: '#0a1e38',
          border: '1px solid rgba(114, 210, 255, 0.25)',
          borderRadius: '6px',
          padding: '12px',
          marginBottom: '16px',
          minWidth: '500px'
        }}>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            <div><strong>M:</strong> Switch annotation mode</div>
            <div><strong>X:</strong> Remove last annotation</div>
            <div><strong>S:</strong> Save all annotations</div>
          </div>
        </div>
        <p style={{ fontSize: '14px' }}>
          <strong>Tip:</strong> Press <strong>Shift+K</strong> anytime to see all available shortcuts.
        </p>
      </div>
    ),
    highlightElement: 'shortcuts-hint',
    tooltip: 'Press Shift+K to see all keyboard shortcuts',
    tooltipPosition: 'right',
    allowBackdropClick: false,
  },

  {
    id: 'saving-annotations',
    title: 'Saving Your Work',
    content: (
      <div>
        <h2 style={{ color: '#72d2ff', marginBottom: '12px' }}>Saving Annotations</h2>
        <p style={{ marginBottom: '16px' }}>
          Save your annotations to a JSON file by pressing <strong>S</strong>. You can also upload previously saved annotations using the upload section.
        </p>
      </div>
    ),
    highlightElement: 'upload-annotations',
    tooltip: 'Use this section to upload previously saved annotations',
    tooltipPosition: 'right',
    allowBackdropClick: false,
  },

  // Phase 6: Tips and Best Practices
  {
    id: 'tips',
    title: 'Tips and Best Practices',
    content: (
      <div>
        <h2 style={{ color: '#72d2ff', marginBottom: '12px' }}>Tips for Success</h2>
        <div style={{
          background: '#0a1e38',
          border: '1px solid rgba(114, 210, 255, 0.25)',
          borderRadius: '6px',
          padding: '12px',
          marginBottom: '16px',
          minWidth: '500px'
        }}>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            <div style={{ marginBottom: '8px' }}><strong>1. Use keyboard shortcuts:</strong> They significantly speed up your workflow</div>
            <div style={{ marginBottom: '8px' }}><strong>2. Frame-by-frame navigation:</strong> Use arrow keys for precise annotations</div>
            <div style={{ marginBottom: '8px' }}><strong>3. Save regularly:</strong> Press S frequently to save your progress</div>
            <div style={{ marginBottom: '8px' }}><strong>4. Use filters:</strong> Filter annotations to review specific types</div>
          </div>
        </div>
        <p style={{ fontSize: '14px' }}>
          Use the Feedback button at the bottom if you have questions or suggestions!
        </p>
      </div>
    ),
    highlightElement: null,
    allowBackdropClick: false,
  },
];

export const useTutorial = () => {
  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [waitingForAction, setWaitingForAction] = useState(false);

  const startTutorial = useCallback(() => {
    setIsActive(true);
    setCurrentStepIndex(0);
    setWaitingForAction(false);
    usageTrackingService.trackTutorial('start', 0);
  }, []);

  const stopTutorial = useCallback(() => {
    setIsActive(false);
    setCurrentStepIndex(0);
    setWaitingForAction(false);
  }, []);

  const nextStep = useCallback(() => {
    if (currentStepIndex < TUTORIAL_STEPS.length - 1) {
      const nextIndex = currentStepIndex + 1;
      setCurrentStepIndex(nextIndex);
      setWaitingForAction(false);
      usageTrackingService.trackTutorial('step', nextIndex);
    } else {
      usageTrackingService.trackTutorial('complete', TUTORIAL_STEPS.length);
      stopTutorial();
    }
  }, [currentStepIndex, stopTutorial]);

  const previousStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
      setWaitingForAction(false);
    }
  }, [currentStepIndex]);

  const skipTutorial = useCallback(() => {
    usageTrackingService.trackTutorial('skip', currentStepIndex);
    stopTutorial();
  }, [stopTutorial, currentStepIndex]);

  const getCurrentStep = useCallback(() => {
    return TUTORIAL_STEPS[currentStepIndex];
  }, [currentStepIndex]);

  const handleActionComplete = useCallback(() => {
    if (waitingForAction) {
      setWaitingForAction(false);
      // Auto-advance after a short delay
      setTimeout(() => {
        nextStep();
      }, 500);
    }
  }, [waitingForAction, nextStep]);

  return {
    isActive,
    currentStepIndex,
    totalSteps: TUTORIAL_STEPS.length,
    currentStep: getCurrentStep(),
    waitingForAction,
    startTutorial,
    stopTutorial,
    nextStep,
    previousStep,
    skipTutorial,
    handleActionComplete,
  };
};
